import { createContext, useCallback, useContext, useEffect } from "react";
import type {
	AudioPlaybackService,
	BlendedAudioPlaybackService,
	PlaybackStatus,
} from "@/lib/audio/application/ports";
import { useApp } from "@/app/providers/app-provider";
import type { AudioModel } from "@coffitivity/shared";
import type { IAudioRepository } from "@/lib/audio/infrastructure/AudioRepository";

interface AudioContextProps {
	audioRepository: IAudioRepository;
	createAdapter: (
		track: AudioModel,
	) => Promise<AudioPlaybackService | BlendedAudioPlaybackService>;
	currentTrack: AudioModel | null;
	handleMuteToggle: () => void;
	handlePause: (baseTrackId?: string, blendTrackId?: string) => void;
	handleMasterVolumeChange: (volume: number) => void;
	handlePlay: (track: AudioModel, blendTrackId?: string) => void;
	audioStatus: PlaybackStatus;
	masterVolume: number;
}

const AudioContext = createContext<AudioContextProps | undefined>(undefined);

export const useAudio = () => {
	const context = useContext(AudioContext);
	if (!context) {
		throw new Error("useAudio must be used within an AudioProvider");
	}
	return context;
};

const AudioProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const { audioManager } = useApp();
	const { audioRepository } = audioManager;
	const { currentTrack, masterVolume, status } = audioManager.useStore();
	// // User Engagement Handler
	// const initializeAudioOnUserGesture = useCallback(async () => {
	// 	// Only run once!
	// 	if (isUserEngaged) return;

	// 	setIsUserEngaged(true);
	// }, [isUserEngaged]);

	// // Wire up user gesture listeners early (only once)
	// useEffect(() => {
	// 	if (!isUserEngaged) {
	// 		window.addEventListener("click", initializeAudioOnUserGesture, {
	// 			once: true,
	// 		});
	// 		window.addEventListener("touchstart", initializeAudioOnUserGesture, {
	// 			once: true,
	// 		});
	// 	}
	// 	// cleanup in case component unmounts before engagement
	// 	return () => {
	// 		window.removeEventListener("click", initializeAudioOnUserGesture);
	// 		window.removeEventListener("touchstart", initializeAudioOnUserGesture);
	// 	};
	// }, [isUserEngaged, initializeAudioOnUserGesture]);

	// Keyboard
	const handleKeyboardEvents = useCallback(
		(event: KeyboardEvent) => {
			const target = event.target as HTMLElement;
			const isTypingElement = ["INPUT", "TEXTAREA", "SELECT"].includes(
				target.tagName,
			);
			if (isTypingElement) return;
			if (event.key === " " || event.code === "Space") {
				event.preventDefault();
				if (status === "PLAYING") {
					handlePause();
				} else {
					if (currentTrack) {
						handlePlay(currentTrack as AudioModel);
					}
				}
			}
		},
		[status, currentTrack],
	);

	// Volume/mute logic
	const handleMasterVolumeChange = (volume: number) => {
		const normalizedVolume = volume > 1 ? volume / 100 : volume;
		audioManager?.setMasterVolume(normalizedVolume);
	};

	const handleMuteToggle = () => {
		audioManager?.toggleMute();
	};

	// Pause logic
	const handlePause = (baseTrackId?: string, blendTrackId?: string) => {
		console.log("Pausing track", baseTrackId, blendTrackId);
		audioManager?.pauseTrack(baseTrackId, blendTrackId);
	};

	// Play logic
	const handlePlay = async (track: AudioModel, blendTrackId?: string) => {
		// if (!isUserEngaged) {
		// 	await initializeAudioOnUserGesture();
		// }

		audioManager?.playTrack(track, blendTrackId);
	};

	// Adapter creation
	const handleAdapterCreation = async (track: AudioModel) => {
		const adapter = await audioManager.getOrCreateAudioAdapter(track);

		return adapter;
	};

	// Keyboard events
	useEffect(() => {
		window.addEventListener("keydown", handleKeyboardEvents);
		return () => window.removeEventListener("keydown", handleKeyboardEvents);
	}, [handleKeyboardEvents]);

	return (
		<AudioContext.Provider
			value={{
				audioRepository,
				audioStatus: status,
				createAdapter: handleAdapterCreation,
				currentTrack: currentTrack as AudioModel | null,
				handleMasterVolumeChange,
				handleMuteToggle,
				handlePause,
				handlePlay,
				masterVolume,
			}}
		>
			{children}
		</AudioContext.Provider>
	);
};

export default AudioProvider;
